import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactDatePicker from "react-datepicker";
import NumberFormat from "react-number-format";
import { useSelector } from "react-redux";
import { DYNAMIC_FIELD_TYPES } from "../../../common/constants";
import moment from "moment";
import {
    getStaticRecommendationBandExcludeKeys,
    sortDynamicRecommendationBandEntries
} from "../../../../utils/recommendationBandUtils";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import Editor from "ckeditor5-custom-build/build/ckeditor";
import Portal from "../../../common/components/Portal";
import { RichEditorPopup } from "../../../common/components/formFields/RichEditorPopup";
import SelectBox from "../../../images/components/SelectBox";
import { evaluateAST, getASTRefs, thousandsSeparators } from "../../../../config/utils";
import { CkeditorConfiguration } from "../../../smartCharts/CkEditorConfig";
import { isBuildingViewFieldVisible } from "../../../building/fieldVisibility/buildingViewFieldConfigUtils";
import { dynamicFieldConfigKey } from "../../../building/fieldVisibility/buildingViewFieldConfig";
import { isAssetViewFieldVisible } from "../../../assets/fieldVisibility/assetViewFieldConfigUtils";
import { dynamicFieldConfigKey as assetDynamicFieldConfigKey } from "../../../assets/fieldVisibility/assetViewFieldConfig";
import CalendarIcon from "../../../../assets/img/Group7.svg";
import ReactTooltip from "react-tooltip";

const EMPTY_BAND_KEYS = [];

export const DynamicBand = ({
    bandData,
    bandShown,
    handleBandClick,
    handleChangeData,
    showErrorBorder,
    multiselectClass = null,
    isFieldLocked,
    isFieldRequired,
    isFieldTouched,
    isRecommendation = false,
    isMultiRecommendation = false,
    isAsset = false,
    isBuilding = false,
    isBuildingMultiEdit = false,
    isCR = false,
    staticEntityEnabled,
    isLease = false,
    searchField = "",
    isMR = false,
    new_dynamic_field_design = false,
    excludeBandKeys,
    buildingViewFieldConfig,
    buildingViewFieldConfigReady = false,
    assetViewFieldConfig,
    assetViewFieldConfigReady = false
}) => {
    const dynamicFields = useSelector(state => state.recommendationsReducer?.dynamicFields);
    const recommendation_custom_fields = dynamicFields?.recommendation_custom_fields ?? {};
    const { asset_custom_field = {} } = useSelector(state => state.assetReducer?.assetDynamicFields) || {};
    const { building_custom_field = {} } = useSelector(state => state.buildingReducer?.buildingDynamicFields) || {};
    const { lease_custom_fields = {} } = useSelector(state => state.leaseBuildingReducer?.getLeaseDynamicFieldsResponse) || {};
    const customFields = isAsset
        ? asset_custom_field
        : isBuilding
        ? building_custom_field
        : isLease
        ? lease_custom_fields
        : recommendation_custom_fields;
    let rawBands;

    if (!searchField) {
        rawBands = customFields || {};
    } else {
        const matchingBandEntries = Object.entries(customFields || {})?.filter(([bandName, fields]) =>
            fields?.some(field => field?.label?.toLowerCase()?.includes(searchField?.toLowerCase()))
        );

        rawBands = Object.fromEntries(matchingBandEntries);
    }

    const effectiveExcludeBandKeys =
        excludeBandKeys ?? (!isAsset && !isBuilding && !isLease ? getStaticRecommendationBandExcludeKeys(dynamicFields ?? {}) : EMPTY_BAND_KEYS);
    const excludeBandKeySet = useMemo(() => new Set(effectiveExcludeBandKeys.map(String)), [effectiveExcludeBandKeys]);

    const bands = useMemo(() => {
        if (isBuilding) {
            if (isBuildingMultiEdit) {
                return rawBands;
            }
            if (!buildingViewFieldConfigReady || !buildingViewFieldConfig?.config) {
                return {};
            }
            const filtered = {};
            Object.entries(rawBands || {}).forEach(([band, fields]) => {
                const visibleFields = (fields || []).filter(field =>
                    isBuildingViewFieldVisible(dynamicFieldConfigKey(field.field_name), buildingViewFieldConfig)
                );
                if (visibleFields.length) filtered[band] = visibleFields;
            });
            return filtered;
        }
        if (isAsset) {
            if (!assetViewFieldConfigReady || !assetViewFieldConfig?.config) {
                return rawBands;
            }
            const filtered = {};
            Object.entries(rawBands || {}).forEach(([band, fields]) => {
                const visibleFields = (fields || []).filter(field =>
                    isAssetViewFieldVisible(assetDynamicFieldConfigKey(field.field_name), assetViewFieldConfig)
                );
                if (visibleFields.length) filtered[band] = visibleFields;
            });
            return filtered;
        }
        if (!isRecommendation && !isMultiRecommendation) {
            return rawBands;
        }

        const entries = Object.entries(rawBands)
            .filter(([band]) => !excludeBandKeySet.has(String(band)))
            .map(([band, fields]) => {
                const filtered = fields.filter(field => {
                    const rules = field.rules || {};
                    const vt = rules.visible_type ?? "both";
                    const visible = vt === "both" || (isCR ? vt === "cr_only" : vt === "recommendation_only");
                    if (!visible) return false;
                    if (isMultiRecommendation && rules.is_sort_order) return false;
                    if (!isAsset && !isBuilding) {
                        if (field.link_static_entity === "energy" && !staticEntityEnabled?.energy) {
                            return false;
                        }
                        if (field.link_static_entity === "water" && !staticEntityEnabled?.water) {
                            return false;
                        }
                    }
                    return true;
                });
                return [band, filtered];
            })
            .filter(([, filtered]) => filtered.length > 0);

        const sorted = sortDynamicRecommendationBandEntries(entries, dynamicFields ?? {});
        return Object.fromEntries(sorted);
    }, [
        rawBands,
        isRecommendation,
        isMultiRecommendation,
        isCR,
        staticEntityEnabled,
        isAsset,
        isBuilding,
        isBuildingMultiEdit,
        excludeBandKeySet,
        dynamicFields,
        buildingViewFieldConfig,
        buildingViewFieldConfigReady,
        assetViewFieldConfig,
        assetViewFieldConfigReady
    ]);

    const [showRichTextPopup, setShowRichTextPopup] = useState({
        value: "",
        label: "",
        key: "",
        onSaveData: () => {},
        show: false
    });
    const getBandId = band => band.split(" ").join("_");

    const toggleShowRichTextPopup = value => setShowRichTextPopup(value);

    const renderRichTextPopup = () => {
        const { label, key, onSaveData, show, value } = showRichTextPopup;
        if (!show) return null;
        return (
            <Portal
                body={
                    <RichEditorPopup
                        value={value}
                        label={label}
                        key={key}
                        onCancel={() => toggleShowRichTextPopup({ ...showRichTextPopup, show: false })}
                        saveData={onSaveData}
                    />
                }
                onCancel={() => toggleShowRichTextPopup({ ...showRichTextPopup, show: false })}
            />
        );
    };

    return (
        <>
            {renderRichTextPopup()}
            {Object.entries(bands)?.map(([band, fields]) => {
                let usedHeader = 0;
                const headerElems = [];
                let skipHeader = false;
                for (const elem of fields) {
                    const origSpan = Number(elem.rules?.column_span) || 1;
                    if (origSpan > 3) {
                        skipHeader = true;
                        usedHeader = 3;
                        break;
                    }
                    const span = Math.min(origSpan, 3);
                    if (usedHeader + span <= 3) {
                        usedHeader += span;
                        headerElems.push(elem);
                    } else {
                        break;
                    }
                }
                const remainingHeaderCells = 3 - usedHeader;

                const bodyCandidates = fields.filter(e => !headerElems.includes(e));
                const bodyRows = [];
                let currentRow = [];
                let currentSpan = 0;

                bodyCandidates.forEach(elem => {
                    const span = Math.min(Number(elem.rules?.column_span) || 1, 4);
                    if (currentSpan + span > 4) {
                        bodyRows.push(currentRow);
                        currentRow = [];
                        currentSpan = 0;
                    }
                    currentRow.push({ elem, span });
                    currentSpan += span;
                });
                if (currentRow.length) bodyRows.push(currentRow);

                return (
                    <div className={`card ${isBuilding && !new_dynamic_field_design ? "band-building-sep" : ""}`} key={band}>
                        <div className="card-header" id={`heading-${getBandId(band)}`}>
                            <div className="otr-recom-div">
                                <button
                                    className="btn btn-link"
                                    data-toggle="collapse"
                                    data-target={`#${getBandId(band)}`}
                                    aria-expanded="false"
                                    onClick={e => {
                                        e.preventDefault();
                                        handleBandClick(getBandId(band), !bandShown?.[getBandId(band)]);
                                    }}
                                >
                                    {band}
                                </button>

                                {(!isBuilding || new_dynamic_field_design) &&
                                    !skipHeader &&
                                    headerElems.map(elem => {
                                        const span = Math.min(Number(elem.rules?.column_span) || 1, 3);
                                        const colSize = span * 3;
                                        return (
                                            <DynamicBandField
                                                key={elem.field_name}
                                                bandShown={bandShown?.[getBandId(band)]}
                                                bandId={getBandId(band)}
                                                fieldType={elem.field_type}
                                                onChange={(key, value) =>
                                                    handleChangeData({
                                                        dynamic_fields: { ...bandData, [key]: value }
                                                    })
                                                }
                                                value={bandData?.[elem?.field_name] || ""}
                                                fieldKey={elem.field_name}
                                                label={elem.label}
                                                handleBandClick={() => handleBandClick(getBandId(band), true)}
                                                fieldItem={elem}
                                                showErrorBorder={showErrorBorder}
                                                multiselectClass={multiselectClass}
                                                isFieldLocked={isFieldLocked}
                                                isFieldRequired={isFieldRequired}
                                                isFieldTouched={isFieldTouched}
                                                isMultiRecommendation={isMultiRecommendation}
                                                isBuildingMultiEdit={isBuildingMultiEdit}
                                                isCR={isCR}
                                                showRichTextPopup={showRichTextPopup}
                                                toggleShowRichTextPopup={toggleShowRichTextPopup}
                                                isRecommendation={isRecommendation}
                                                bandData={bandData}
                                                colSize={colSize}
                                                isLease={isLease}
                                                isMR={isMR}
                                                new_dynamic_field_design={new_dynamic_field_design}
                                            />
                                        );
                                    })}

                                {(!isBuilding || new_dynamic_field_design) &&
                                    [...Array(remainingHeaderCells)].map((_, i) => <div key={`hdr-fill-${i}`} className="txt-rcm col-md-3" />)}
                            </div>
                        </div>

                        <div
                            id={getBandId(band)}
                            className={bandShown?.[getBandId(band)] || isBuilding && !new_dynamic_field_design ? "collapse show" : "collapse"}
                            aria-labelledby={`heading-${getBandId(band)}`}
                        >
                            <div className="card-body p-0">
                                <div className={`outer-rcm mt-0 ${isBuilding ? "d-flex flex-wrap" : ""}`}>
                                    {isBuilding && !new_dynamic_field_design ? (
                                        fields.map(elem => (
                                            <DynamicBandField
                                                key={elem.field_name}
                                                fieldType={elem.field_type}
                                                onChange={(key, value) =>
                                                    handleChangeData({
                                                        dynamic_fields: { ...bandData, [key]: value }
                                                    })
                                                }
                                                value={bandData?.[elem?.field_name] || ""}
                                                fieldKey={elem.field_name}
                                                label={elem.label}
                                                handleBandClick={() => handleBandClick(getBandId(band), true)}
                                                fieldItem={elem}
                                                showErrorBorder={showErrorBorder}
                                                multiselectClass={multiselectClass}
                                                isFieldLocked={isFieldLocked}
                                                isFieldRequired={isFieldRequired}
                                                isFieldTouched={isFieldTouched}
                                                isMultiRecommendation={isMultiRecommendation}
                                                isBuildingMultiEdit={isBuildingMultiEdit}
                                                isCR={isCR}
                                                isBuilding={isBuilding}
                                                toggleShowRichTextPopup={toggleShowRichTextPopup}
                                                bandData={bandData}
                                                isMR={isMR}
                                            />
                                        ))
                                    ) : (
                                        <>
                                            {bodyRows.map((row, rowIdx) => (
                                                <React.Fragment key={rowIdx}>
                                                    {row.map(({ elem, span }) => {
                                                        const colSize = span * 3;
                                                        return (
                                                            <DynamicBandField
                                                                key={elem.field_name}
                                                                fieldType={elem.field_type}
                                                                onChange={(key, value) =>
                                                                    handleChangeData({
                                                                        dynamic_fields: { ...bandData, [key]: value }
                                                                    })
                                                                }
                                                                value={bandData?.[elem?.field_name] || ""}
                                                                fieldKey={elem.field_name}
                                                                label={elem.label}
                                                                handleBandClick={() => handleBandClick(getBandId(band), true)}
                                                                fieldItem={elem}
                                                                showErrorBorder={showErrorBorder}
                                                                multiselectClass={multiselectClass}
                                                                isFieldLocked={isFieldLocked}
                                                                isFieldRequired={isFieldRequired}
                                                                isFieldTouched={isFieldTouched}
                                                                isMultiRecommendation={isMultiRecommendation}
                                                                isBuildingMultiEdit={isBuildingMultiEdit}
                                                                isCR={isCR}
                                                                isBuilding={isBuilding}
                                                                toggleShowRichTextPopup={toggleShowRichTextPopup}
                                                                bandData={bandData}
                                                                colSize={colSize}
                                                                isLease={isLease}
                                                                isMR={isMR}
                                                                new_dynamic_field_design={new_dynamic_field_design}
                                                            />
                                                        );
                                                    })}
                                                    {(() => {
                                                        const used = row.reduce((sum, r) => sum + r.span, 0);
                                                        const cols = (4 - used) * 3;
                                                        return cols > 0 ? <div className={`txt-rcm col-md-${cols} dynamic-cell`} /> : null;
                                                    })()}
                                                </React.Fragment>
                                            ))}
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                );
            })}
        </>
    );
};

export const DynamicBandField = ({
    fieldType = "text",
    label,
    fieldKey,
    value,
    onChange,
    bandShown,
    bandId,
    handleBandClick,
    fieldItem,
    showErrorBorder,
    multiselectClass,
    isFieldLocked = undefined,
    isFieldRequired = undefined,
    isFieldTouched = undefined,
    isMultiRecommendation = false,
    isCR = false,
    showRichTextPopup = false,
    isRecommendation = false,
    toggleShowRichTextPopup,
    isBuilding = false,
    isBuildingMultiEdit = false,
    bandData = {},
    colSize,
    isLease = false,
    isMR = false,
    new_dynamic_field_design = false
}) => {
    const {
        mandatory: ruleMandatory = false,
        prefix = "",
        suffix = "",
        thousand_separator = false,
        read_only = false,
        decimal_scale = 0,
        allow_negative = true,
        show_lease_chart = false
    } = fieldItem?.rules || {};
    const configuredRequired = isFieldRequired ? isFieldRequired(fieldKey, isCR) : false;
    const mandatory = configuredRequired || ruleMandatory;
    const shouldValidate = !isMultiRecommendation || !isFieldTouched || isFieldTouched(fieldKey);
    const isError = showErrorBorder && mandatory && shouldValidate && (!value || (value && !value?.toString()?.trim()?.length));
    const isLocked = isFieldLocked && isFieldLocked(fieldKey);
    const hasInitialized = useRef(false);
    // Raw while focused so a value like "N/A" can still be typed; NumberFormat would refuse it.
    const [isEditingText, setIsEditingText] = useState(false);
    const separatedTextValue =
        thousand_separator && !isEditingText && value !== "" && value !== null && value !== undefined && !isNaN(value)
            ? thousandsSeparators(value)
            : value;

    const ast = fieldItem.rules.equation;
    const deps = useMemo(() => Array.from(getASTRefs(ast)), [ast]);
    const result = useMemo(() => {
        if (!ast) return "";

        for (const name of deps) {
            const v = bandData[name];
            if (v === "" || v === null || v === undefined) {
                return "";
            }
        }
        const val = evaluateAST(ast, bandData);
        return Number.isFinite(val) ? val : "";
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ast, ...deps.map(name => bandData[name])]);

    useEffect(() => {
        if (fieldItem?.field_mode !== "calculated") return;
        const current = bandData[fieldKey];

        if (result !== "" && result !== current) {
            onChange(fieldKey, result);
        } else if (result === "" && current !== "" && !isBuildingMultiEdit) {
            onChange(fieldKey, "");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [result, fieldItem.field_mode, fieldKey, onChange]);

    return (
        <div
            className={`txt-rcm ${fieldItem.className ? fieldItem.className : ""} ${
                isBuilding && !new_dynamic_field_design ? "col-md-4 pl-0 mb-2 " : `col-md-${colSize} dynamic-cell`
            }`}
            key={fieldKey}
        >
            <ReactTooltip id="dynamic_field" effect="solid" place="top" backgroundColor="#007bff" className="rc-tooltip-custom-class" />
            <div
                className={`content-inp-card ${isBuilding && !new_dynamic_field_design && fieldType === DYNAMIC_FIELD_TYPES.SELECT ? "col-md-12 p-0" : ""}`}
            >
                <div
                    className={`form-group ${fieldType === DYNAMIC_FIELD_TYPES.MEMO ? "ckeditor-band recom-ck-band " : ""}`}
                    data-target={bandShown ? `#${bandId}` : "false"}
                    aria-expanded={bandShown ? true : "false"}
                    onClick={() => handleBandClick()}
                    data-tip={isLocked ? "This field is locked and cannot be edited" : ""}
                    data-for="recommendation_details"
                >
                    <label>
                        {`${label} ${mandatory ? "*" : ""}`}
                        {isLease && show_lease_chart && (
                            <img
                                data-for="dynamic_field"
                                data-tip="This field represents a chart event or milestone and will be displayed in lease-related charts and graphs. You can change this setting in the dynamic field settings."
                                className="ml-2 mb-1"
                                src={CalendarIcon}
                                height={15}
                                width={15}
                                alt=""
                                srcset=""
                            />
                        )}
                        {fieldType === DYNAMIC_FIELD_TYPES.MEMO && (
                            <button
                                class="butn-expansion"
                                data-for="input_field"
                                data-tip="Expand Field"
                                onClick={e => {
                                    e.preventDefault();
                                    toggleShowRichTextPopup({
                                        value: value,
                                        label: label,
                                        key: fieldKey,
                                        onSaveData: value => {
                                            onChange(fieldKey, value);
                                            toggleShowRichTextPopup({ ...showRichTextPopup, value, show: false });
                                        },
                                        show: true
                                    });
                                }}
                            >
                                <img src="/img/expand1.svg" alt="" />
                            </button>
                        )}
                    </label>
                    {fieldType === DYNAMIC_FIELD_TYPES.TEXT ? (
                        <input
                            type="text"
                            autoComplete={"off"}
                            className={`custom-input form-control ${isError ? "error-border" : ""} ${
                                multiselectClass ? multiselectClass(fieldKey, null, "dynamic_field") : ""
                            } ${isLocked ? "grayed-out" : ""}`}
                            placeholder={label}
                            value={separatedTextValue}
                            disabled={read_only || isLocked}
                            name={fieldKey}
                            onChange={e => onChange(e.target.name, e.target.value)}
                            onFocus={() => setIsEditingText(true)}
                            onBlur={() => setIsEditingText(false)}
                        />
                    ) : fieldType === DYNAMIC_FIELD_TYPES.MEMO ? (
                        <div
                            className={`custom-input form-control ${read_only ? "cursor-diabled" : ""} ${isError ? "error-border" : ""} ${
                                multiselectClass ? multiselectClass(fieldKey, null, "dynamic_field") : ""
                            } ${isLocked ? "grayed-out" : ""}`}
                        >
                            <CKEditor
                                editor={Editor}
                                config={{ ...CkeditorConfiguration, toolbar: [] }}
                                id="report-note-template"
                                data={value ?? ""}
                                onChange={(event, editor) => {
                                    if (!hasInitialized?.current) {
                                        hasInitialized.current = true;
                                        return;
                                    }

                                    const data = editor.getData();
                                    onChange(fieldKey, data);
                                }}
                                onReady={editor => {
                                    if (editor && !isMR) {
                                        editor.setData(value || "");
                                    }
                                }}
                            />
                        </div>
                    ) : fieldType === DYNAMIC_FIELD_TYPES.SELECT ? (
                        <div
                            className={`${multiselectClass ? multiselectClass(fieldKey, null, "dynamic_field") : ""} ${
                                isLocked ? "grayed-out" : ""
                            } sel-recom-react`}
                        >
                            <SelectBox
                                className={`${isError ? "error-border" : ""}`}
                                value={value}
                                disabled={read_only || isLocked}
                                handleChange={value => onChange(fieldKey, value)}
                                optionsList={fieldItem?.field_options ?? []}
                                isClearable={!isRecommendation}
                                showErrorBorder={isError}
                                hasEmptySelect
                                isRecommendation
                                isDynamicField
                            />
                        </div>
                    ) : fieldType === DYNAMIC_FIELD_TYPES.BOOLEAN ? (
                        <div
                            className={`${multiselectClass ? multiselectClass(fieldKey, null, "dynamic_field") : ""} ${
                                isLocked ? "grayed-out" : ""
                            } sel-recom-react`}
                        >
                            <SelectBox
                                className={`${isError ? "error-border" : ""}`}
                                value={value}
                                disabled={read_only || isLocked}
                                handleChange={value => onChange(fieldKey, value)}
                                optionsList={["Yes", "No"].map(e => ({ label: e, value: e }))}
                                isClearable={!isRecommendation}
                                showErrorBorder={isError}
                                hasEmptySelect
                                isRecommendation
                                isDynamicField
                            />
                        </div>
                    ) : fieldItem.field_mode === "calculated" ? (
                        <NumberFormat
                            autoComplete={"off"}
                            className={`custom-input form-control cursor-diabled ${isError ? "error-border" : ""} ${
                                multiselectClass ? multiselectClass(fieldKey, null, "dynamic_field") : ""
                            } ${isLocked ? "grayed-out" : ""}`}
                            placeholder={label}
                            thousandSeparator={thousand_separator}
                            prefix={prefix}
                            suffix={suffix}
                            value={(result !== "" ? result : isBuildingMultiEdit ? value : "") || ""}
                            disabled={true}
                            displayType={"input"}
                            readOnly
                        />
                    ) : fieldType === DYNAMIC_FIELD_TYPES.NUMBER ? (
                        <NumberFormat
                            autoComplete={"off"}
                            className={`custom-input form-control ${read_only ? "cursor-diabled" : ""} ${isError ? "error-border" : ""} ${
                                multiselectClass ? multiselectClass(fieldKey, null, "dynamic_field") : ""
                            } ${isLocked ? "grayed-out" : ""}`}
                            placeholder={label}
                            thousandSeparator={thousand_separator}
                            prefix={prefix}
                            suffix={suffix}
                            decimalScale={decimal_scale}
                            value={value || ""}
                            disabled={read_only || isLocked}
                            displayType={"input"}
                            allowNegative={allow_negative}
                            onValueChange={(values, sourceInfo) => {
                                const { value } = values;
                                onChange(fieldKey, value);
                            }}
                        />
                    ) : fieldType === DYNAMIC_FIELD_TYPES.CURRENCY ? (
                        <NumberFormat
                            autoComplete={"off"}
                            className={`custom-input form-control ${read_only ? "cursor-diabled" : ""} ${isError ? "error-border" : ""} ${
                                multiselectClass ? multiselectClass(fieldKey, null, "dynamic_field") : ""
                            } ${isLocked ? "grayed-out" : ""}`}
                            placeholder={label}
                            thousandSeparator={true}
                            prefix={"$ "}
                            decimalScale={decimal_scale}
                            allowNegative={allow_negative}
                            suffix={suffix}
                            value={value || ""}
                            disabled={read_only || isLocked}
                            displayType={"input"}
                            onValueChange={values => {
                                const { value } = values;
                                onChange(fieldKey, value);
                            }}
                        />
                    ) : fieldType === DYNAMIC_FIELD_TYPES.DATE ? (
                        <ReactDatePicker
                            autoComplete={"nope"}
                            placeholderText={label}
                            className={`${isError ? "error-border" : ""}custom-input form-control custom-wid ${
                                multiselectClass ? multiselectClass(fieldKey, null, "dynamic_field") : ""
                            }  ${isLocked ? "grayed-out" : ""}`}
                            selected={value ? new Date(value) : ""}
                            disabled={read_only || isLocked}
                            showMonthDropdown
                            showYearDropdown
                            dropdownMode="scroll"
                            maxDate={moment().add(100, "years").toDate()}
                            minDate={moment().subtract(100, "years").toDate()}
                            onChange={date => {
                                const formatted = date ? moment(date).format("YYYY-MM-DD") : "";
                                onChange(fieldKey, formatted);
                            }}
                        />
                    ) : null}
                </div>
            </div>
        </div>
    );
};
